﻿import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { detectCrisis } from '../services/safety/crisisDetection.js';
import CrisisResourceCard from '../components/CrisisResourceCard.jsx';
import {
  ChevronLeft,
  Send,
  Mic,
  Lock,
  MoreHorizontal,
  Volume2,
  VolumeX,
  Keyboard,
  X,
  ArrowRight,
} from 'lucide-react';
import FontStyles from '../components/shared/FontStyles.jsx';
import FoxMark from '../components/shared/FoxMark.jsx';
import ReactiveFoxMark from '../components/shared/ReactiveFoxMark.jsx';
import {
  PAPER,
  PAPER_2,
  INK,
  INK_MID,
  INK_SOFT,
  LINE,
  ACCENT,
  GOLD_DEEP,
} from '../components/shared/theme.js';
import { useApp } from '../context/AppContext.jsx';
import { useVoice } from '../hooks/useVoice.js';
import useVoiceConversation from '../hooks/useVoiceConversation.js';
import { useWhisperRecorder } from '../hooks/useWhisperRecorder.js';
import HoldToSpeak from '../components/shared/HoldToSpeak.jsx';
import {
  conductIntake,
  extractProfile,
  generateStudy,
} from '../services/anthropic.js';
import { transcribeAudio } from '../services/openai.js';
import {
  speakWithElevenLabs,
  speakIntoElement,
  keepAudioSessionAlive,
  stopAllAudio,
} from '../services/elevenlabs.js';
import { warmupAudioContext, detachAmplitude } from '../services/audioAmplitude.js';

const OPENING_LINE = `Hey — I am so glad you are here. I am genuinely excited to get to know you and build something just for you. From this day forward I will create a daily study built around exactly where you are — your season, your story, what you are carrying right now. At the end of each day you will have a chance to tell me what landed, what you are dealing with, something you want to go deeper on in your spiritual journey, or even a book you are reading that you want woven into your study. I will carry all of that into the next day. Nothing gets wasted. And just so you know — this is just for you. Nothing you share here leaves this space. Ever. The more honest you are, the more specific this becomes. And specific is where it actually helps. So let us start with the simplest question — what is going on in your life right now?`;

const HEADER_STACK_HEIGHT = 138;

// Mirrors api/speak.js's VOICE_IDS map. The streaming endpoint at
// /api/speak-stream takes a literal ElevenLabs voice ID (not a gender
// alias), so gender → ID resolution happens here.
const VOICE_IDS = {
  male: 'L0Dsvb3SLTyegXwtm47J',
  female: 'ys3XeJJA4ArWMhRpcX1D',
};

// Mobile browsers handle continuous-mode speech recognition poorly and pause
// it when the screen locks or the tab backgrounds. We adapt the loop to those
// constraints when running on a mobile UA.
const isIOS =
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isAndroid =
  typeof navigator !== 'undefined' &&
  /Android/i.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;

// 100-byte silent MP3 used to gesture-unlock the persistent intake audio
// element inside the "Talk with Ada" tap. iOS unlocks the SPECIFIC
// HTMLAudioElement that played first inside a gesture; we then reuse
// that element for every subsequent ElevenLabs utterance by swapping
// its .src — even when the play() call lands well after the original
// gesture window has closed (which is why audio.play() was rejecting
// with NotAllowedError when the TTS fetch took too long).
const SILENT_MP3 =
  'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQCgAAAAAAAAAGGmGOG3AAAAAAA//tAxAAACMArn3UAgAhTBSh3O0AAEEAQAAGDgGAYBgcAGB4D5SCATBcF/E6XB85znD8H/E5/y/y4Ifk4PggCAIAgD4Pg+/E4f//Lg+CAIBQNg+D4Pg+CAIAgD4Pg+D4f5cHwf/y4P8uCAIAgCAID4Pg+D4Pg+CAIBh//5cHwQBAEAQBAEAQDgIA///UCAIAh//+CAYDg+CAIBQEAQDH//+CAID/+CAIBQEAQDg//9KBAEAYHwQBAEAYHwfB//KAgCAIAgGB8EAQBAEAQDA+D4PggCAYHwfB8EAQBAGB8HwfB8EAYAB';

// =====================================================================
// Completion screen — used at the end of intake when the iOS audio
// session can't reliably play long passages back-to-back. Renders the
// guide's words on screen as the source of truth, attempts audio in
// the background (no awaiting), and waits for the user to tap through.
// =====================================================================
function CompletionMessage({
  text,
  buttonLabel,
  guide,
  onContinue,
  shouldSpeak = true,
  intakeAudioRef, // dormant — Day 6 removes
  speakFn,
}) {
  const hasSpokenRef = useRef(false);

  useEffect(() => {
    if (hasSpokenRef.current) return;
    hasSpokenRef.current = true;

    // Text-mode users finish intake in silence. The on-screen card is
    // the source of truth either way; this just suppresses audio.
    if (!shouldSpeak) return;
    if (typeof speakFn !== 'function') {
      console.warn('[Completion] no speakFn — completion audio skipped');
      return;
    }

    (async () => {
      try {
        await speakFn(text);
      } catch (e) {
        console.warn('[Completion] speakFn threw:', e?.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ textAlign: 'center', maxWidth: 360 }}>
      <p
        style={{
          fontFamily: 'Georgia, serif',
          fontSize: 17,
          color: '#1B1814',
          lineHeight: 1.8,
          fontStyle: 'italic',
          marginBottom: 32,
        }}
      >
        &ldquo;{text}&rdquo;
      </p>
      <p
        style={{
          fontSize: 12,
          color: '#A67C3A',
          fontFamily: 'Arial, sans-serif',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 16,
          opacity: 0.7,
        }}
      >
        — {guide?.name || 'Your guide'}
      </p>
      <button
        type="button"
        onClick={onContinue}
        style={{
          background: '#1B1814',
          color: '#F2EBDC',
          border: 'none',
          borderRadius: 12,
          padding: '14px 32px',
          fontSize: 16,
          cursor: 'pointer',
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
          width: '100%',
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

const COMPLETION_TRANSITION_TEXT =
  'Thank you for sharing that with me. I have everything I need. Give me a moment — but first I want to say something important.';
const COMPLETION_DISCLAIMER_TEXT = `Before we go further — Rooted is built on scripture and the wisdom of people who have walked hard roads. But I am not a counselor and this is not a substitute for one. If you are carrying something that needs more than a study, please reach out to someone who can truly sit with you. What is ahead here matters. And I am honored to be part of it.`;

// New end-of-intake transition. Voice-mode users hear this immediately
// after the final guide reply, then are routed to /enneagram. The
// duty-of-care disclaimer (COMPLETION_DISCLAIMER_TEXT above) now lives
// at the top of EnneagramAssessment.jsx instead of on its own card.
const ENNEAGRAM_TRANSITION_LINE =
  "I have everything I need. Before we begin, I'd love for you to take a quick Enneagram assessment — it'll help me tune the study to who you really are.";

// =====================================================================
// Local styles
// =====================================================================
function IntakeStyles() {
  return (
    <style>{`
      @keyframes typingDot {
        0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
        30% { opacity: 1; transform: translateY(-3px); }
      }
      .typing-dot { animation: typingDot 1.2s ease-in-out infinite; }
      .typing-dot:nth-child(2) { animation-delay: 0.18s; }
      .typing-dot:nth-child(3) { animation-delay: 0.36s; }

      @keyframes micPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(166,124,58,0.55); }
        50% { box-shadow: 0 0 0 10px rgba(166,124,58,0); }
      }
      .mic-pulse { animation: micPulse 1.4s ease-out infinite; }

      @keyframes micRestartPulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.18); }
        100% { transform: scale(1); }
      }
      .mic-restart { animation: micRestartPulse 0.65s ease-in-out 1; }

      @keyframes preparingPulse {
        0%, 100% { transform: scale(1); opacity: 0.92; }
        50% { transform: scale(1.05); opacity: 1; filter: drop-shadow(0 0 16px rgba(166,124,58,0.35)); }
      }
      .preparing-pulse { animation: preparingPulse 2.6s ease-in-out infinite; }

      @keyframes msgIn {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .msg-in { animation: msgIn 380ms cubic-bezier(0.2, 0.8, 0.2, 1); }

      @keyframes progressShimmer {
        0% { background-position: -200px 0; }
        100% { background-position: 200px 0; }
      }
      .progress-shimmer {
        background-image: linear-gradient(90deg, rgba(166,124,58,0.6) 0%, rgba(201,164,94,0.95) 50%, rgba(166,124,58,0.6) 100%);
        background-size: 200px 100%;
        animation: progressShimmer 2.4s ease-in-out infinite;
      }

      @keyframes avatarSpeakPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(166,124,58,0.55); transform: scale(1); }
        50% { box-shadow: 0 0 0 8px rgba(166,124,58,0); transform: scale(1.06); }
      }
      .avatar-speaking { animation: avatarSpeakPulse 1.6s ease-in-out infinite; border-radius: 50%; }

      @keyframes speakerWave {
        0%, 100% { transform: scaleY(0.4); }
        50% { transform: scaleY(1); }
      }
      .speaker-bar { animation: speakerWave 0.8s ease-in-out infinite; transform-origin: center; }
      .speaker-bar:nth-child(2) { animation-delay: 100ms; }
      .speaker-bar:nth-child(3) { animation-delay: 200ms; }

      @keyframes orbBreathSpeak {
        0%, 100% { transform: scale(1); box-shadow: 0 0 60px 0 rgba(166,124,58,0.55); }
        50% { transform: scale(1.06); box-shadow: 0 0 90px 10px rgba(201,164,94,0.7); }
      }
      .orb-speaking { animation: orbBreathSpeak 2s ease-in-out infinite; }

      @keyframes orbBreathListen {
        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(166,124,58,0.45); }
        50% { transform: scale(1.04); box-shadow: 0 0 0 18px rgba(166,124,58,0); }
      }
      .orb-listening { animation: orbBreathListen 1.6s ease-out infinite; }

      @keyframes waveBar {
        0%, 100% { height: 6px; }
        50% { height: 22px; }
      }
      .wave-bar { animation: waveBar 0.9s ease-in-out infinite; }

      @keyframes countdownArc {
        from { stroke-dashoffset: 0; }
        to { stroke-dashoffset: 113; }
      }
      .countdown-arc { animation: countdownArc 5s linear forwards; }

      @keyframes cardIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .card-in { animation: cardIn 420ms cubic-bezier(0.2, 0.8, 0.2, 1); }
    `}</style>
  );
}

// =====================================================================
// Speaker indicator (next to guide name)
// =====================================================================
function SpeakerWaveIcon() {
  return (
    <span
      className="inline-flex items-end gap-[2px] ml-1"
      style={{ height: 11 }}
      aria-hidden="true"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="speaker-bar"
          style={{
            display: 'inline-block',
            width: 2,
            height: 11,
            background: ACCENT,
            borderRadius: 1,
          }}
        />
      ))}
    </span>
  );
}

// =====================================================================
// Progress bar
// =====================================================================
function progressLabel(pct) {
  if (pct <= 30) return 'Getting to know you…';
  if (pct <= 60) return 'Going deeper…';
  if (pct <= 85) return 'Almost there…';
  return 'Building your study…';
}

// Tiny status line that surfaces the current voice engine for testing.
function VoiceStatusPill({ guideGender }) {
  const voiceName = guideGender === 'male' ? 'ARCHER' : 'JESS';
  return (
    <div
      className="px-5 pb-3 pt-0 w-full max-w-[440px] mx-auto text-center"
      aria-live="polite"
    >
      <span
        className="ff-mono inline-flex items-center gap-1.5"
        style={{
          color: GOLD_DEEP,
          fontSize: 9.5,
          letterSpacing: '0.16em',
        }}
      >
        <span aria-hidden="true">🔊</span>
        VOICE READY — {voiceName} (ELEVENLABS)
      </span>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="px-7 pb-3 w-full max-w-[440px] mx-auto">
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="ff-mono"
          style={{
            color: GOLD_DEEP,
            fontSize: 9.5,
            letterSpacing: '0.18em',
          }}
        >
          {progressLabel(pct).toUpperCase()}
        </span>
        <span
          className="ff-mono"
          style={{ color: INK_SOFT, fontSize: 9.5, letterSpacing: '0.12em' }}
        >
          {Math.round(pct)}%
        </span>
      </div>
      <div
        className="h-[3px] w-full rounded-full overflow-hidden"
        style={{ background: 'rgba(27,24,20,0.08)' }}
      >
        <div
          className="h-full rounded-full progress-shimmer transition-all"
          style={{
            width: `${pct}%`,
            transitionDuration: '700ms',
            transitionTimingFunction: 'cubic-bezier(0.2,0.8,0.2,1)',
          }}
        />
      </div>
    </div>
  );
}

// =====================================================================
// Bubbles + typing
// =====================================================================
function GuideMessage({ content, guideName, isLatest, isSpeaking, isLoading }) {
  const showLive = isLatest && isSpeaking;
  const showLoading = isLatest && isLoading;
  return (
    <div className="msg-in flex items-start gap-3 mb-4">
      <div
        style={{ flexShrink: 0, marginTop: 2 }}
        className={showLive || showLoading ? 'avatar-speaking' : ''}
      >
        <FoxMark size={32} variant="gold" />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="ff-mono inline-flex items-center gap-1.5"
          style={{
            color: ACCENT,
            fontSize: 9.5,
            letterSpacing: '0.18em',
            marginBottom: 4,
          }}
        >
          {(guideName || 'GUIDE').toUpperCase()}
          {showLive && <SpeakerWaveIcon />}
          {showLoading && (
            <span
              className="inline-flex items-center gap-1"
              aria-hidden="true"
              style={{ marginLeft: 2 }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="typing-dot"
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: ACCENT,
                    display: 'inline-block',
                  }}
                />
              ))}
            </span>
          )}
        </div>
        <div
          className="ff-display rounded-2xl rounded-tl-sm px-4 py-3"
          style={{
            background: PAPER_2,
            border: `1px solid ${LINE}`,
            color: INK,
            fontSize: 15,
            lineHeight: 1.5,
            fontWeight: 400,
            whiteSpace: 'pre-wrap',
          }}
        >
          {content}
        </div>
      </div>
    </div>
  );
}

function UserMessage({ content }) {
  return (
    <div className="msg-in flex justify-end mb-4">
      <div className="max-w-[78%]">
        <div
          className="ff-ui rounded-2xl rounded-tr-sm px-4 py-3"
          style={{
            background: INK,
            color: PAPER,
            fontSize: 14.5,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {content}
        </div>
      </div>
    </div>
  );
}

function TypingIndicator({ guideName }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <FoxMark size={32} variant="gold" />
      </div>
      <div>
        <div
          className="ff-mono"
          style={{
            color: ACCENT,
            fontSize: 9.5,
            letterSpacing: '0.18em',
            marginBottom: 4,
          }}
        >
          {(guideName || 'GUIDE').toUpperCase()}
        </div>
        <div
          className="rounded-2xl rounded-tl-sm px-4 py-3 inline-flex items-center gap-1.5"
          style={{
            background: PAPER_2,
            border: `1px solid ${LINE}`,
          }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="typing-dot"
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: ACCENT,
                display: 'inline-block',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Loading overlay — fixed to viewport
// =====================================================================
function PreparingScreen({ guideName, errorMessage }) {
  return (
    <div
      className="grain flex flex-col items-center justify-center px-9 text-center"
      style={{ background: PAPER, position: 'fixed', inset: 0, zIndex: 100 }}
    >
      <div className="preparing-pulse">
        <FoxMark size={108} variant="gold" />
      </div>
      <span className="block hairline-gold h-px w-32 mt-8" />
      <div
        className="mt-6 ff-mono"
        style={{ color: INK_SOFT, fontSize: 10, letterSpacing: '0.22em' }}
      >
        {errorMessage ? 'ONE MOMENT' : 'BUILDING YOUR FIRST WEEK'}
      </div>
      {errorMessage ? (
        <p
          className="ff-display mt-4 leading-[1.5] max-w-[340px]"
          style={{ color: INK, fontSize: 18, fontWeight: 400 }}
        >
          {errorMessage}
        </p>
      ) : (
        <>
          <p
            className="ff-display mt-4 leading-[1.45] max-w-[320px]"
            style={{ color: INK, fontSize: 20, fontWeight: 400 }}
          >
            {guideName || 'Your guide'} is preparing your study…
          </p>
          <p
            className="ff-display italic mt-4 leading-[1.55] max-w-[320px]"
            style={{ color: INK_MID, fontSize: 14 }}
          >
            This usually takes about 5 minutes. Feel free to step away — your
            study will be waiting.
          </p>
        </>
      )}
    </div>
  );
}

// =====================================================================
// Mode selection cards
// =====================================================================
function ModeSelector({ guideName, onSelect, onSelectVoice, micPermissionDenied }) {
  return (
    <div
      className="card-in w-full max-w-[440px] mx-auto px-5 flex flex-col"
      style={{
        paddingTop: HEADER_STACK_HEIGHT + 18,
        paddingBottom: 24,
        minHeight: '100vh',
      }}
    >
      {/* TOP — mode selector (heading + buttons) */}
      <div className="text-center mb-5">
        <span className="block hairline-gold h-px w-24 mx-auto" />
        <div
          className="mt-4 ff-mono"
          style={{ color: GOLD_DEEP, fontSize: 10, letterSpacing: '0.22em' }}
        >
          HOW WOULD YOU LIKE TO TALK?
        </div>
        <p
          className="ff-display italic mt-2"
          style={{ color: INK_MID, fontSize: 13 }}
        >
          Pick what feels natural — you can switch anytime.
        </p>
      </div>

      {micPermissionDenied && (
        <div
          className="ff-display mb-4"
          style={{
            padding: '14px 16px',
            background: 'rgba(166,124,58,0.10)',
            border: `1px solid rgba(166,124,58,0.30)`,
            borderRadius: 12,
            textAlign: 'center',
            fontSize: 13.5,
            color: INK,
            lineHeight: 1.5,
          }}
        >
          Microphone access was denied. You can still type your responses
          below, or enable microphone access in your browser settings and try
          again.
        </div>
      )}

      <div className="space-y-3">
        <button
          onClick={() => onSelect('text')}
          className="w-full text-left rounded-2xl p-5 transition-all active:scale-[0.99]"
          style={{
            background: PAPER_2,
            border: `1px solid ${LINE}`,
            boxShadow: '0 4px 14px -10px rgba(27,24,20,0.18)',
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: 'rgba(166,124,58,0.10)',
                color: GOLD_DEEP,
                flexShrink: 0,
              }}
            >
              <Keyboard size={20} strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div
                className="ff-display"
                style={{ color: INK, fontSize: 17, fontWeight: 500 }}
              >
                Type your responses
              </div>
              <p
                className="ff-display italic mt-1.5 leading-[1.45]"
                style={{ color: INK_MID, fontSize: 13.5 }}
              >
                Prefer to write? Type at your own pace.
              </p>
            </div>
            <ArrowRight
              size={18}
              strokeWidth={1.6}
              style={{ color: INK_SOFT, marginTop: 12 }}
            />
          </div>
        </button>

        {/* PREMIUM GATE: Voice conversation mode — premium tier only.
            Currently enabled for all tiers for testing. */}
        <button
          onClick={() => onSelectVoice()}
          className="w-full text-left rounded-2xl p-5 transition-all active:scale-[0.99]"
          style={{
            background: PAPER_2,
            border: `1px solid ${ACCENT}`,
            boxShadow: '0 6px 18px -10px rgba(166,124,58,0.5)',
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: ACCENT,
                color: PAPER,
                flexShrink: 0,
              }}
            >
              <Mic size={20} strokeWidth={1.7} />
            </div>
            <div className="flex-1">
              <div
                className="ff-display flex items-center gap-2"
                style={{ color: INK, fontSize: 17, fontWeight: 500 }}
              >
                Talk with {guideName || 'your guide'}
                <span
                  className="ff-mono px-1.5 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(166,124,58,0.12)',
                    color: GOLD_DEEP,
                    fontSize: 8.5,
                    letterSpacing: '0.15em',
                  }}
                >
                  HANDS-FREE
                </span>
              </div>
              <p
                className="ff-display italic mt-1.5 leading-[1.45]"
                style={{ color: INK_MID, fontSize: 13.5 }}
              >
                Prefer to speak? Have a real conversation.
              </p>
            </div>
            <ArrowRight
              size={18}
              strokeWidth={1.6}
              style={{ color: ACCENT, marginTop: 12 }}
            />
          </div>
        </button>
      </div>

      {/* SPACER — pushes the guide's opening message to the bottom */}
      <div className="flex-1" />

      {/* BOTTOM — guide's opening message */}
      <div className="mt-6">
        <GuideMessage
          content={OPENING_LINE}
          guideName={guideName}
          isLatest={false}
          isSpeaking={false}
        />
      </div>
    </div>
  );
}

// =====================================================================
// Standard text input bar (with persistent mic)
// =====================================================================
function InputBar({ onSend, disabled }) {
  const [value, setValue] = useState('');

  // Whisper-based mic — tap to start, tap again to stop + transcribe + paste
  const {
    startRecording,
    stopRecording,
    cancel: cancelRecording,
    isRecording,
    isTranscribing,
  } = useWhisperRecorder();

  const isSupported =
    typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined';

  useEffect(() => {
    return () => cancelRecording();
  }, [cancelRecording]);

  const toggleMic = async () => {
    if (disabled) return;
    if (isRecording) {
      const text = await stopRecording();
      if (text) {
        setValue((v) => (v ? v.trim() + ' ' + text : text));
      }
    } else {
      await startRecording();
    }
  };

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    if (isRecording) cancelRecording();
    onSend(text);
    setValue('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const displayValue = value;

  return (
    <div
      className="px-5 pt-3 pb-5"
      style={{
        background: `linear-gradient(to top, ${PAPER} 60%, rgba(242,235,220,0))`,
      }}
    >
      <div
        className="flex items-end gap-2 px-3 py-2 rounded-3xl"
        style={{
          background: PAPER_2,
          border: `1px solid ${LINE}`,
          boxShadow: '0 4px 14px -8px rgba(27,24,20,0.18)',
        }}
      >
        <textarea
          value={displayValue}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={
            isRecording
              ? 'Recording — tap mic to stop'
              : isTranscribing
              ? 'Transcribing…'
              : 'Tell me what is going on'
          }
          disabled={disabled || isTranscribing}
          className="flex-1 ff-ui resize-none bg-transparent outline-none px-2 py-2"
          style={{
            color: INK,
            fontSize: 14.5,
            lineHeight: 1.45,
            maxHeight: 120,
            minHeight: 36,
          }}
        />
        {isSupported && (
          <button
            onClick={toggleMic}
            disabled={disabled || isTranscribing}
            aria-label={isRecording ? 'Stop recording' : 'Start recording'}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95 ${
              isRecording ? 'mic-pulse' : ''
            }`}
            style={{
              background: isRecording ? ACCENT : 'transparent',
              color: isRecording ? PAPER : INK_MID,
              border: `1px solid ${isRecording ? ACCENT : LINE}`,
              flexShrink: 0,
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            <Mic size={16} strokeWidth={1.75} />
          </button>
        )}
        <button
          onClick={submit}
          disabled={!value.trim() || disabled}
          aria-label="Send"
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: value.trim() && !disabled ? INK : 'rgba(27,24,20,0.15)',
            color: value.trim() && !disabled ? PAPER : INK_SOFT,
            flexShrink: 0,
          }}
        >
          <Send size={16} strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// VOICE MODE — orb panel
// PREMIUM GATE: Voice conversation mode — premium tier only
// Currently enabled for all tiers for testing
// =====================================================================
function VoiceModePanel({
  guideName,
  isGuideSpeaking,
  awaitingMicGrant,
  isRecording,
  isTranscribing,
  amplitude = 0,
  getStream,
  onTranscript,
  onRecorderState,
  onExit,
}) {
  const animClass = isGuideSpeaking
    ? 'orb-speaking'
    : isRecording
    ? 'orb-listening'
    : '';

  const stateLabel = isGuideSpeaking
    ? `${(guideName || 'YOUR GUIDE').toUpperCase()} IS SPEAKING`
    : awaitingMicGrant
    ? 'TAP ALLOW TO CONTINUE…'
    : isRecording
    ? 'RECORDING — RELEASE TO SEND'
    : isTranscribing
    ? 'TRANSCRIBING…'
    : 'YOUR TURN';

  const orbBg = isGuideSpeaking ? ACCENT : isRecording ? PAPER : PAPER_2;
  const orbBorder = isRecording ? '#5B2A2E' : ACCENT;

  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{ minHeight: 460, paddingTop: 24, paddingBottom: 24 }}
    >
      <div
        className="ff-mono"
        style={{ color: GOLD_DEEP, fontSize: 10, letterSpacing: '0.22em' }}
      >
        {stateLabel}
      </div>

      <div
        className={`mt-6 rounded-full flex items-center justify-center ${animClass}`}
        style={{
          width: 220,
          height: 220,
          background: orbBg,
          border: `1px solid ${orbBorder}`,
          boxShadow: isGuideSpeaking
            ? '0 0 60px 0 rgba(166,124,58,0.45)'
            : isRecording
            ? '0 0 0 8px rgba(91,42,46,0.20)'
            : 'none',
          transition: 'background 200ms, border-color 200ms, box-shadow 200ms',
        }}
      >
        <ReactiveFoxMark
          amplitude={amplitude}
          isActive={isGuideSpeaking}
          size={isGuideSpeaking ? 140 : 100}
        />
      </div>

      <div
        className="mt-6 px-7 text-center min-h-[42px]"
        style={{ maxWidth: 360 }}
      >
        <p
          className="ff-display italic leading-[1.4]"
          style={{ color: INK_SOFT, fontSize: 13 }}
        >
          {isGuideSpeaking
            ? 'Listen…'
            : awaitingMicGrant
            ? 'Tap Allow when your phone asks — then we can keep going.'
            : isRecording
            ? 'Take your time. Release the button when you are done.'
            : isTranscribing
            ? 'Hearing what you said…'
            : 'Hold the button below to speak.'}
        </p>
      </div>

      {/* The single primary control — Whisper hold-to-speak */}
      <div className="mt-5">
        <HoldToSpeak
          getStream={getStream}
          onTranscript={onTranscript}
          onStateChange={onRecorderState}
          disabled={isGuideSpeaking || awaitingMicGrant}
        />
      </div>

      <button
        onClick={onExit}
        className="mt-6 ff-ui inline-flex items-center gap-2 px-4 py-2 rounded-full transition active:scale-95"
        style={{
          color: INK_MID,
          fontSize: 12,
          background: 'transparent',
          border: `1px solid ${LINE}`,
        }}
      >
        <X size={13} strokeWidth={1.75} />
        Exit voice mode
      </button>
    </div>
  );
}

// =====================================================================
// MAIN
// =====================================================================
export default function Intake() {
  const navigate = useNavigate();
  const {
    guide,
    intakeMessages,
    setIntakeMessages,
    appendIntakeMessage,
    userProfile,
    updateUserProfile,
    setStudy,
    voiceEnabled,
    toggleVoice,
    inputMode,
    setInputMode,
    dailyFeedback,
  } = useApp();

  const {
    stop: stopSpeaking,
    voicesReady,
    isSpeaking,
    isLoading: isVoiceLoading,
    amplitude,
  } = useVoice();

  // Day-5 audio: useVoiceConversation owns AudioContext, TTS, mic capture,
  // transcription, and intake persistence. Old useVoice destructure above
  // stays so dormant code that reads stopSpeaking / isSpeaking / amplitude
  // type-checks until Day 6 deletes it. authToken is intentionally null:
  // intake is pre-auth, persistence runs IDB-only, Day 7 hardening adds
  // a backfill-on-signup path.
  const voice = useVoiceConversation({
    userId: null,
    authToken: null,
    sessionId: null,
    voiceId: VOICE_IDS[guide?.gender] || VOICE_IDS.female,
    onTurnComplete: () => {
      // Hook already persisted the user turn and updated voice.currentTranscript.
      // Surface the existing "I heard …" confirm card; the live Send-tap
      // handler still owns the actual conversation advance via handleSend.
      setInlineShowConfirm(true);
    },
    onError: (err) => {
      console.error('[Intake/voice]', err);
      setError(err?.message || 'Audio error');
    },
  });
  // Latest-version ref so the inline button's native event listener
  // closure (created once on mount) always reaches the freshest hook
  // state + methods instead of the first-render snapshot.
  const voiceRef = useRef(voice);
  useEffect(() => {
    voiceRef.current = voice;
  });

  const [isTyping, setIsTyping] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparingErrorMessage, setPreparingErrorMessage] = useState('');
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [completionStep, setCompletionStep] = useState(0);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const pendingCompletionArgsRef = useRef(null);
  const completionStartedRef = useRef(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(10);

  // Watchdog state — if the guide hasn't replied within 30s of the user
  // sending, surface a retry button so the conversation isn't stuck.
  const [showRetry, setShowRetry] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState(null);
  const retryTimerRef = useRef(null);

  const clearRetryWatchdog = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setShowRetry(false);
  };

  const bottomRef = useRef(null);
  const lastSpokenRef = useRef(null);

  // Voice-mode runtime state. Driven now from voice.state; the legacy
  // setters in speakAndWait / friendly-error still run from dormant
  // paths but the useEffect below overrides them on the next tick.
  const [isGuideSpeaking, setIsGuideSpeaking] = useState(false);
  const isGuideSpeakingRef = useRef(false);
  useEffect(() => {
    const speaking = voice.state === 'speaking';
    setIsGuideSpeaking(speaking);
    isGuideSpeakingRef.current = speaking;
  }, [voice.state]);

  // Persistent <audio> element for ElevenLabs playback. Constructed
  // synchronously inside the user's first voice-entry tap (see
  // requestMicPermissionAndEnterVoiceMode / handleModeSelect /
  // switchToVoice) so iOS marks it as gesture-unlocked. Every subsequent
  // utterance in the intake reuses this element by swapping .src, which
  // dodges the NotAllowedError that fires when audio.play() runs after
  // the TTS fetch latency has expired the original gesture window.
  const intakeAudioRef = useRef(null);

  // Creates the persistent <audio> element on first call and fully awaits
  // its silent prime inside the user gesture. The .play() promise MUST
  // settle before anything else touches the element — iOS treats an
  // aborted prime (interrupted by a later .src swap / .load() / .pause())
  // as a broken gesture chain and silently mutes the next real play().
  // Stores the prime promise on the ref so downstream callers
  // (speakAndWait, speakIntoElement) can also await it defensively.
  const ensurePrimedAudioElement = async () => {
    if (intakeAudioRef.current) return;
    const audio = new Audio(SILENT_MP3);
    audio.__rootedId = `intake-audio-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    console.log('[Intake] created audio element', audio.__rootedId);
    audio.preload = 'auto';
    audio.playsInline = true;
    audio.muted = true;
    intakeAudioRef.current = audio;
    const primePromise = (async () => {
      try {
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        console.log('[Intake] persistent element primed cleanly', audio.__rootedId);
      } catch (e) {
        console.warn(
          '[Intake] persistent element prime failed:',
          e?.name,
          e?.message,
          audio.__rootedId
        );
      }
    })();
    // Expose on both the ref (for speakAndWait) and the element itself
    // (for the speakIntoElement defensive await), so any callsite that
    // touches the primed element can wait for it to fully settle.
    intakeAudioRef.primePromise = primePromise;
    audio.primePromise = primePromise;
    console.log('[Intake] helper: about to await primePromise', audio.__rootedId);
    await primePromise;
    console.log('[Intake] helper: await primePromise resolved, returning', audio.__rootedId);
  };

  // Mirror inputMode in a ref so async functions (speakAndWait, the
  // post-AI speak path, etc.) read the current value rather than the
  // stale closure capture from when the call started.
  const inputModeRef = useRef(inputMode);
  useEffect(() => {
    inputModeRef.current = inputMode;
  }, [inputMode]);
  const [voiceRecorderState, setVoiceRecorderState] = useState({
    isRecording: false,
    isTranscribing: false,
  });
  const handleSendRef = useRef(null);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [awaitingMicGrant, setAwaitingMicGrant] = useState(false);

  // Seed the opening message + reset transient intake state on every
  // mount. Without this, navigating away from the intake and back
  // within the same SPA session leaves inputMode === 'voice' from a
  // prior pass, ModeSelector is skipped, and the user lands directly
  // on the "tap to talk" hold-button view. Intake is a one-time flow
  // that ends with the user being released to the rest of the app, so
  // restarting on remount is the right behavior — there's no resume
  // path to preserve.
  useEffect(() => {
    setInputMode(null);
    setIntakeMessages([{ role: 'assistant', content: OPENING_LINE }]);
    return () => {
      try {
        voice.cancel();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Removed) The previous "speak the latest guide message in TEXT mode"
  // useEffect was what caused the guide to talk during text-mode intake.
  // Text mode is now silent end-to-end; voice mode handles its own
  // speak-then-listen cycle inside speakAndWait.

  // If voice is muted, cancel in-flight speech.
  useEffect(() => {
    if (!voiceEnabled) {
      try {
        voice.cancel();
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceEnabled]);

  // Auto-scroll
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [intakeMessages, isTyping]);

  // -------------- DORMANT: legacy Android AudioContext unlock --------------
  // Pre-Path-B workaround for Android Chrome: an inline AudioContext
  // create + resume + silent-oscillator sequence inside the user gesture
  // so the first ElevenLabs play() call wasn't blocked. Path B's
  // AudioSessionManager handles this for every platform via the
  // persistent sink element + ConstantSourceNode, so this helper is no
  // longer wired into any live handler. Kept verbatim for Day 6 deletion.
  // eslint-disable-next-line no-unused-vars
  async function legacyAndroidUnlock() {
    if (!isAndroid) return;
    console.log('[Intake.Voice] Android branch: unlocking AudioContext');
    await new Promise((r) => setTimeout(r, 200));
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        await ctx.resume();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        gainNode.gain.value = 0;
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.001);
        await new Promise((r) => setTimeout(r, 100));
        await ctx.close();
        console.log('[Intake.Voice] Android AudioContext unlock complete');
      }
    } catch (e) {
      console.warn('[Intake.Voice] AudioContext unlock failed:', e);
    }
  }

  // -------------- speakAndWait helper --------------
  // Routes through the gesture-unlocked persistent element
  // (intakeAudioRef) via speakIntoElement so iOS doesn't reject play()
  // with NotAllowedError when TTS-fetch latency outlives the original
  // tap's gesture window. Falls back to legacy speakWithElevenLabs only
  // if the persistent element somehow wasn't primed.
  async function speakAndWait(text) {
    if (!voiceEnabled || !text) return;
    // Hard gate — the guide must never speak in text mode, regardless of
    // who calls speakAndWait or whether voiceEnabled is on.
    if (inputModeRef.current !== 'voice') return;
    lastSpokenRef.current = null; // force-speak
    setIsGuideSpeaking(true);
    isGuideSpeakingRef.current = true;

    const isIOSUA = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      await new Promise(async (resolve) => {
        let resolved = false;
        const safeResolve = (reason) => {
          if (resolved) return;
          resolved = true;
          console.log('[speakAndWait] resolved:', reason);
          resolve();
        };

        if (isIOSUA) {
          try {
            await keepAudioSessionAlive();
            await new Promise((r) => setTimeout(r, 400));
          } catch {}
        }

        if (!intakeAudioRef.current) {
          // Fallback: no primed element (shouldn't happen, but degrade
          // gracefully rather than going silent).
          console.warn(
            '[speakAndWait] no persistent element — falling back to legacy path'
          );
          try {
            await speakWithElevenLabs(text, guide?.gender || 'female', () =>
              safeResolve('legacy-onEnd')
            );
          } catch {
            safeResolve('legacy-error');
          }
          // Generous safety timer for the legacy path too.
          const wordCount = (text || '').trim().split(/\s+/).length;
          setTimeout(
            () => safeResolve('legacy-safety-timer'),
            wordCount * 350 + (isIOSUA ? 15000 : 8000)
          );
          return;
        }

        // Defensive: if the prime is somehow still in flight, wait for
        // it to settle before mutating the element. Touching .muted /
        // .src / .load() while the prime's play() promise is pending
        // aborts it on iOS and burns the gesture token, which silently
        // mutes the real TTS playback that follows.
        if (intakeAudioRef.primePromise) await intakeAudioRef.primePromise;
        console.log('[muted-set] site=speakAndWait', {
          refIsNull: intakeAudioRef.current === null,
          refIsUndefined: intakeAudioRef.current === undefined,
          elementId: intakeAudioRef.current && intakeAudioRef.current.__rootedId,
          readyState: intakeAudioRef.current && intakeAudioRef.current.readyState,
          paused: intakeAudioRef.current && intakeAudioRef.current.paused,
        });
        intakeAudioRef.current.muted = false;
        await speakIntoElement(
          text,
          guide?.gender || 'female',
          intakeAudioRef.current,
          () => safeResolve('onEnd')
        );

        // Safety timer — 350ms/word + 8s. If the element's onended fails
        // to fire (silent-resolve bug we hit on iOS), this still releases
        // the await so the intake conversation continues.
        const wordCount = (text || '').trim().split(/\s+/).length;
        setTimeout(
          () => safeResolve('safety-timer'),
          wordCount * 350 + 8000
        );
      });
    } catch (e) {
      console.warn('[speakAndWait] Error:', e);
    }

    setIsGuideSpeaking(false);
    isGuideSpeakingRef.current = false;
  }

  // -------------- Completion sequence (called from both text and voice paths) --------------
  // Runs through the transition + disclaimer + explanation, then either
  // navigates straight to /enneagram (already signed in) or shows the
  // auth-explain overlay so the user can sign up / skip.
  const runCompletionSequence = async (reply, baseMessagesAtTurnStart, userMsg) => {
    if (completionStartedRef.current) {
      console.log('[Completion] Already started — ignoring duplicate call');
      return;
    }
    completionStartedRef.current = true;

    console.log('[Completion] Starting');
    void reply;
    void baseMessagesAtTurnStart;
    void userMsg;

    // Step 1 — stop any active inline recorder + release stream
    inlineIsRecordingRef.current = false;
    if (
      inlineMediaRecorderRef.current &&
      inlineMediaRecorderRef.current.state !== 'inactive'
    ) {
      try {
        inlineMediaRecorderRef.current.stop();
      } catch {}
    }
    inlineReleaseStream();

    // Step 2 — cancel anything the voice pipeline is doing so the
    // completion message doesn't overlap the previous turn.
    try {
      voice.cancel();
    } catch {}

    // Step 3-5 (legacy): detachAmplitude + iOS settle + warmupAudioContext
    // were workarounds for the old elements-based pipeline. The Path B
    // architecture keeps the audio session claimed via the persistent
    // sink element + ConstantSourceNode, so no settle / re-warm is
    // needed. A small await preserves UI sequencing.
    await new Promise((r) => setTimeout(r, 200));

    // Step 6 — show the completion screen. The transition + disclaimer
    // are now rendered as text the user reads (audio is best-effort
    // only, attempted from inside CompletionMessage).
    setProgress(95);
    setCompletionStep(0);
    setShowCompletionScreen(true);
  };

  // -------------- Continue button tap (intake completion) --------------
  // The fire-and-forget speak that used to live inside handleSend's
  // isComplete branch was hitting NotAllowedError on iOS because by the
  // time /api/speak returned, the original user gesture had expired.
  // The speak now runs here, gesture-bound to the Continue tap, before
  // we transition into runCompletionSequence.
  const handleContinueTap = async () => {
    // Dismiss the button immediately so a stray double-tap can't fire
    // the speak / sequence twice.
    setShowContinueButton(false);

    // Cancel anything the voice pipeline has in flight so the new
    // speak doesn't layer on top.
    try {
      voice.cancel();
    } catch {}

    if (inputModeRef.current === 'voice') {
      const finalReply = [...intakeMessages]
        .reverse()
        .find((m) => m.role === 'assistant')?.content;

      if (finalReply && voiceEnabled) {
        try {
          await voice.speak(finalReply);
        } catch (e) {
          console.warn('[handleContinueTap] speak failed:', e?.message);
        }
      }
    } else {
      // Text-mode: small settle so any prior render cleanup completes
      // before runCompletionSequence mounts the next surface.
      await new Promise((r) => setTimeout(r, 200));
    }

    const args = pendingCompletionArgsRef.current || {};
    await runCompletionSequence(null, args.baseMessages, args.userMsg);
  };

  // -------------- Send pipeline (used by text mode + the voice silence flush) --------------
  const handleSend = async (text, { fromVoiceLoop = false } = {}) => {
    if (isTyping || isPreparing) return;
    setError(null);

    const userMsg = { role: 'user', content: text };
    const baseMessages = intakeMessages;
    appendIntakeMessage(userMsg);
    setProgress((p) => Math.min(85, p + 15));

    // Arm the retry watchdog — if 30s pass with no guide reply, surface
    // a retry button. Cleared on success and on the friendly-error path.
    setLastUserMessage(text);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    setShowRetry(false);
    retryTimerRef.current = setTimeout(() => {
      console.warn('[Intake] retry watchdog fired — no guide reply in 30s');
      setShowRetry(true);
    }, 30000);

    const apiMessages = [...baseMessages, userMsg].filter(
      (_, i, arr) => !(i === 0 && arr[0].role === 'assistant')
    );

    setIsTyping(true);
    try {
      const { reply, isComplete } = await conductIntake(
        apiMessages,
        userProfile,
        guide
      );
      setIsTyping(false);
      clearRetryWatchdog();

      if (isComplete) {
        console.log(
          '[Intake] INTAKE COMPLETE — speak final reply + transition, then /enneagram'
        );
        // Path B's persistent audio session means a post-gesture
        // voice.speak() now works without a fresh user tap, so the
        // legacy Continue-button + completion-screen sequence is gone.
        // Final reply + transition line are spoken sequentially, then
        // we navigate. Text mode skips both speaks and routes immediately.
        if (reply) {
          appendIntakeMessage({ role: 'assistant', content: reply });
          if (
            (inputModeRef.current === 'voice' || fromVoiceLoop) &&
            voiceEnabled
          ) {
            // Wrap speak() in a timeout. If the hook's speak promise doesn't settle
            // (e.g., TTS stream dies mid-playback and the state machine fails to
            // reject the pending awaiter — a known hook contract gap), force-proceed
            // after 60s so navigation isn't blocked indefinitely. The user has
            // already seen the reply text in their message history.
            const speakWithTimeout = (text, timeoutMs = 60000) =>
              Promise.race([
                voice.speak(text),
                new Promise((_, reject) =>
                  setTimeout(
                    () => reject(new Error('speak timeout')),
                    timeoutMs
                  )
                ),
              ]);

            try {
              await speakWithTimeout(reply);
            } catch (e) {
              console.warn(
                '[Intake] final reply speak failed or timed out:',
                e?.message
              );
              // Defensively reset the state machine in case it got stuck in 'error'
              // state, so the transition speak below has a chance to run.
              try {
                voice.reset();
              } catch (resetErr) {
                console.warn(
                  '[Intake] voice.reset after first speak fail threw:',
                  resetErr?.message
                );
              }
            }

            try {
              await speakWithTimeout(ENNEAGRAM_TRANSITION_LINE);
            } catch (e) {
              console.warn(
                '[Intake] transition speak failed or timed out:',
                e?.message
              );
            }
          }
        }
        navigate('/enneagram', {
          state: {
            fromVoice:
              (inputModeRef.current === 'voice' || fromVoiceLoop) &&
              voiceEnabled,
          },
        });
        return;
      }

      // Normal reply path — only speak in voice mode. Text mode is silent.
      if (reply) {
        appendIntakeMessage({ role: 'assistant', content: reply });
        if (
          (inputModeRef.current === 'voice' || fromVoiceLoop) &&
          voiceEnabled
        ) {
          console.log('[Intake] Speaking reply');
          try {
            await voice.speak(reply);
          } catch (e) {
            console.warn('[Intake] voice.speak threw:', e?.message);
          }
          console.log('[Intake] Reply spoken — ready for next input');
        }
      }
    } catch (e) {
      console.error('[Intake] handleSend error:', e);
      setIsTyping(false);
      setIsGuideSpeaking(false);
      clearRetryWatchdog();

      const isOverloaded =
        e?.status === 529 ||
        e?.message === 'overloaded' ||
        /overloaded|busy/i.test(e?.message || '');
      const friendlyMessage = isOverloaded
        ? `I am sorry — I am momentarily overwhelmed. Give me just a moment and try again.`
        : `Something went wrong. Please try again.`;

      appendIntakeMessage({ role: 'assistant', content: friendlyMessage });
    }
  };
  handleSendRef.current = handleSend;

  // Wrapper used by HoldToSpeak's onTranscript. Lets us add explicit logging
  // around the voice-mode send path and keeps the latest closure in a ref so
  // native event listeners always see the current handler.
  const sendUserResponse = async (text) => {
    console.log('[Intake] sendUserResponse:', text);
    if (!text || !text.trim()) {
      console.warn('[Intake] Empty text — ignoring');
      return;
    }
    if (handleSendRef.current) {
      console.log(
        '[Intake] Routing through handleSend (fromVoiceLoop: true), current message count:',
        messagesRef.current.length
      );
      await handleSendRef.current(text.trim(), { fromVoiceLoop: true });
      console.log('[Intake] handleSend returned');
    } else {
      console.error('[Intake] handleSendRef is null — send dropped');
    }
  };
  const sendUserResponseRef = useRef(null);
  sendUserResponseRef.current = sendUserResponse;

  // Latest-version ref over intakeMessages so async callers can read the
  // current transcript without waiting for a re-render.
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = intakeMessages;
    console.log('[Intake] messages updated, count:', intakeMessages.length);
  }, [intakeMessages]);

  // ============================================================
  // INLINE recorder for voice mode — copied from /voice-test which
  // works on iOS Safari. No HoldToSpeak component, no React synthetic
  // events on the press surface, no shared hook. Just MediaRecorder
  // + native addEventListener.
  // ============================================================
  const inlineMediaRecorderRef = useRef(null);
  const inlineStreamRef = useRef(null);
  const inlineChunksRef = useRef([]);
  const inlineIsRecordingRef = useRef(false);
  const inlineButtonRef = useRef(null);
  // Latest-version refs for the start/stop functions so the callback-ref
  // closure (created once on button mount) always reaches the current
  // implementation, never a stale closure.
  const inlineStartRecordingRef = useRef(null);
  const inlineStopRecordingRef = useRef(null);
  const [inlineRecordingStatus, setInlineRecordingStatus] =
    useState('Tap to speak');
  const [inlineTranscript, setInlineTranscript] = useState('');
  const [inlineShowConfirm, setInlineShowConfirm] = useState(false);

  async function inlineStartRecording() {
    console.log('[Inline] startRecording called');
    inlineChunksRef.current = [];

    try {
      const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      let stream = null;

      if (isIOSDevice) {
        // iOS — never reuse. iOS silently kills stream tracks between
        // recordings even when track.readyState still reads 'live'. Request
        // a fresh stream every tap.
        console.log('[Inline] iOS — requesting fresh getUserMedia');
        // Tear down any prior stream first so we don't leak handles
        if (inlineStreamRef.current) {
          try {
            inlineStreamRef.current.getTracks().forEach((t) => t.stop());
          } catch {}
          inlineStreamRef.current = null;
        }
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        inlineStreamRef.current = stream;
        console.log('[Inline] New iOS stream obtained');
      } else {
        // Desktop / Android — reuse if every track is live AND enabled
        const existing = inlineStreamRef.current;
        const isAlive =
          existing &&
          existing.active &&
          existing.getTracks().length > 0 &&
          existing
            .getTracks()
            .every((t) => t.readyState === 'live' && t.enabled);
        if (isAlive) {
          stream = existing;
          console.log('[Inline] Reusing existing stream');
        } else {
          if (existing) {
            try {
              existing.getTracks().forEach((t) => t.stop());
            } catch {}
          }
          setInlineRecordingStatus('Requesting mic...');
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          inlineStreamRef.current = stream;
          console.log('[Inline] Requested new stream');
        }
      }

      // Always tear down any previous MediaRecorder and create a fresh one
      if (
        inlineMediaRecorderRef.current &&
        inlineMediaRecorderRef.current.state !== 'inactive'
      ) {
        try {
          inlineMediaRecorderRef.current.stop();
        } catch {}
      }

      const mimeType = isIOSDevice
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';

      console.log('[Inline] Using mimeType:', mimeType || 'default');

      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      inlineMediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) inlineChunksRef.current.push(e.data);
      };

      mr.onstart = () => {
        inlineIsRecordingRef.current = true;
        setInlineRecordingStatus('Listening… tap to send');
        if (inlineButtonRef.current) {
          inlineButtonRef.current.style.background = '#8B6914';
          inlineButtonRef.current.style.color = '#F2EBDC';
          inlineButtonRef.current.style.boxShadow =
            '0 0 0 12px rgba(139,105,20,0.25), 0 0 0 24px rgba(139,105,20,0.10)';
          const span = inlineButtonRef.current.querySelector('span');
          if (span) span.textContent = 'TAP TO STOP';
          const svg = inlineButtonRef.current.querySelector('svg');
          if (svg) svg.setAttribute('stroke', '#F2EBDC');
        }
        console.log('[Inline] MediaRecorder started');
      };

      mr.onerror = (e) => {
        console.error('[Inline] MediaRecorder error:', e?.error || e);
        inlineIsRecordingRef.current = false;
        setInlineRecordingStatus('Error — tap to try again');
        if (inlineStreamRef.current) {
          try {
            inlineStreamRef.current.getTracks().forEach((t) => t.stop());
          } catch {}
          inlineStreamRef.current = null;
        }
      };

      mr.start(1000);
      console.log('[Inline] MediaRecorder.start() called');
    } catch (err) {
      console.error('[Inline] startRecording failed:', err.name, err.message);
      setInlineRecordingStatus('Mic error — tap to try again');
      inlineIsRecordingRef.current = false;
      if (inlineStreamRef.current) {
        try {
          inlineStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        inlineStreamRef.current = null;
      }
    }
  }

  async function inlineStopRecording() {
    console.log('[Inline] stopRecording called');
    inlineIsRecordingRef.current = false;

    if (inlineButtonRef.current) {
      inlineButtonRef.current.style.background = '#F2EBDC';
      inlineButtonRef.current.style.color = '#A67C3A';
      inlineButtonRef.current.style.boxShadow = 'none';
      const span = inlineButtonRef.current.querySelector('span');
      if (span) span.textContent = 'TAP';
      const svg = inlineButtonRef.current.querySelector('svg');
      if (svg) svg.setAttribute('stroke', '#A67C3A');
    }

    if (
      !inlineMediaRecorderRef.current ||
      inlineMediaRecorderRef.current.state === 'inactive'
    ) {
      setInlineRecordingStatus('Tap to speak');
      return;
    }

    setInlineRecordingStatus('Transcribing...');

    inlineMediaRecorderRef.current.onstop = async () => {
      // iOS — release the stream after every recording. iOS silently kills
      // tracks between recordings even when readyState still says 'live',
      // so reusing produces a 'recording' that never lights the mic
      // indicator. Fresh stream per tap is the only reliable pattern.
      // Desktop / Android keep the stream alive across recordings (faster).
      const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOSDevice && inlineStreamRef.current) {
        try {
          inlineStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        inlineStreamRef.current = null;
        console.log('[Inline] iOS — stream released after recording');
      }

      const mimeType =
        inlineMediaRecorderRef.current?.mimeType || 'audio/mp4';
      const blob = new Blob(inlineChunksRef.current, { type: mimeType });

      console.log('[Inline] Blob:', blob.size, 'bytes, type:', mimeType);

      if (blob.size < 500) {
        setInlineRecordingStatus('Too short — try again');
        return;
      }

      try {
        const text = await transcribeAudio(blob);
        console.log('[Inline] Transcript:', text);

        if (text && text.trim()) {
          setInlineTranscript(text.trim());
          setInlineShowConfirm(true);
          setInlineRecordingStatus('Tap to speak');
        } else {
          setInlineRecordingStatus('Nothing heard — try again');
        }
      } catch (err) {
        console.error('[Inline] Whisper error:', err);
        setInlineRecordingStatus('Error — try again');
      }
    };

    try {
      inlineMediaRecorderRef.current.requestData();
    } catch {}

    await new Promise((r) => setTimeout(r, 300));
    inlineMediaRecorderRef.current.stop();
  }

  // Release the long-lived voice-mode mic stream. Called on Exit voice mode,
  // when the intake completes, and on component unmount.
  function inlineReleaseStream() {
    if (inlineStreamRef.current) {
      try {
        inlineStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      inlineStreamRef.current = null;
      console.log('[Inline] Stream released');
    }
  }

  // Keep the latest function versions in refs so the callback-ref closure
  // (which is created once when the button mounts) always reaches the
  // freshest implementations.
  inlineStartRecordingRef.current = inlineStartRecording;
  inlineStopRecordingRef.current = inlineStopRecording;

  // Callback ref — fires the moment the button mounts in the DOM.
  // Native touchend/click listeners (no React synthetic events on the
  // press surface) matches the /voice-test pattern proven on iOS.
  // Reads voiceRef.current.state so the closure always sees fresh hook
  // state even though the callback ref is created once.
  const inlineButtonCallbackRef = useCallback((btn) => {
    if (!btn) return;
    inlineButtonRef.current = btn;
    console.log('[Inline] Button mounted — attaching tap toggle listener');

    async function handleTap(e) {
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      const v = voiceRef.current;
      console.log('[Inline] Button tapped, voice.state:', v?.state);
      if (!v) return;
      try {
        if (v.state === 'listening') {
          await v.stopListening();
        } else {
          await v.startListening();
        }
      } catch (err) {
        console.warn('[Inline] mic toggle threw:', err?.message);
      }
    }

    // touchend (not touchstart) on mobile to avoid accidental triggers from
    // scroll/pan; click for desktop. No mousedown/mouseleave race.
    btn.addEventListener('touchend', handleTap, { passive: false });
    btn.addEventListener('click', handleTap);
  }, []);

  // Strip down a transcript / messages array to a length the API will
  // accept without tripping content filters. We don't try to scrub
  // emotional or spiritual content — only cap the size.
  const sanitizeForAPI = (text) => {
    if (!text) return text;
    return String(text).slice(0, 2000);
  };

  // -------------- Voice mode entry --------------
  // No upfront getUserMedia. Mic is requested only inside
  // inlineStartRecording when the user actually holds the button.
  // Otherwise iOS would have an active stream sitting open during
  // ElevenLabs playback, and the second getUserMedia (when the user
  // pressed Hold) would silently revoke the first — which is what
  // produced the 'orange light turns off when I press hold' bug.
  const requestMicPermissionAndEnterVoiceMode = async () => {
    // CRITICAL: voice.init() MUST be the first audio call inside this
    // gesture — it creates the AudioContext, claims the iOS audio
    // session for Playback (via the persistent sink element +
    // ConstantSourceNode in AudioSessionManager), and transitions the
    // state machine idle → unlocked. Every later speak/listen call
    // rides that claimed session, so no per-utterance gesture is needed.
    console.log('[Intake.Voice] tap → starting voice mode');
    console.log('[Intake.Voice] UA:', navigator.userAgent);
    try {
      await voice.init();
      console.log('[Intake.Voice] voice.init() returned');
    } catch (e) {
      console.warn('[Intake.Voice] voice.init() threw:', e);
    }

    setInputMode('voice');
    // Sync the ref immediately so handleSend's voice-mode gate sees
    // 'voice' on this tick rather than the stale render value.
    inputModeRef.current = 'voice';
    console.log(
      '[Intake.Voice] inputModeRef set to voice; voiceEnabled=',
      voiceEnabled
    );

    const lastGuide =
      [...intakeMessages].reverse().find((m) => m.role === 'assistant')
        ?.content || OPENING_LINE;
    console.log(
      '[Intake.Voice] about to call voice.speak; text len=',
      lastGuide?.length,
      'preview=',
      (lastGuide || '').slice(0, 60)
    );
    if (voiceEnabled) {
      try {
        await voice.speak(lastGuide);
        console.log('[Intake.Voice] voice.speak returned');
      } catch (e) {
        console.warn('[Intake.Voice] voice.speak threw:', e);
      }
    }
    // Hold button now appears; getUserMedia happens inside voice.startListening
    // when the user taps it.
  };

  // -------------- Mode selection / switching --------------
  const handleModeSelect = async (mode) => {
    // Voice-mode entry: voice.init() is the first audio call inside
    // the user gesture (claims the iOS audio session). Text mode skips
    // init entirely — text intake is silent end-to-end.
    if (mode === 'voice') {
      try {
        await voice.init();
      } catch (e) {
        console.warn('[Intake.Voice] voice.init() threw:', e);
      }
    }

    setInputMode(mode);
    // Sync the ref immediately so the handleSend voice-mode gate sees
    // the new mode on this tick rather than the stale render value.
    inputModeRef.current = mode;
    if (mode === 'voice') {
      const lastGuide =
        [...intakeMessages].reverse().find((m) => m.role === 'assistant')
          ?.content || OPENING_LINE;
      if (voiceEnabled) {
        try {
          await voice.speak(lastGuide);
        } catch (e) {
          console.warn('[Intake.Voice] voice.speak threw:', e);
        }
      }
    }
  };

  const switchToText = () => {
    try {
      voice.cancel();
    } catch {}
    setIsGuideSpeaking(false);
    isGuideSpeakingRef.current = false;
    setInputMode('text');
    inputModeRef.current = 'text';
  };

  const switchToVoice = async () => {
    // Toggling text → voice mid-intake is also a fresh user gesture.
    // voice.init() is idempotent if the audio session is already
    // claimed, but calling it here keeps the contract explicit and
    // re-resumes the AudioContext if iOS suspended it.
    try {
      await voice.init();
    } catch (e) {
      console.warn('[Intake.Voice] voice.init() threw:', e);
    }
    setInputMode('voice');
    inputModeRef.current = 'voice';
    const lastGuide =
      [...intakeMessages].reverse().find((m) => m.role === 'assistant')
        ?.content || OPENING_LINE;
    if (voiceEnabled) {
      try {
        await voice.speak(lastGuide);
      } catch (e) {
        console.warn('[Intake.Voice] voice.speak threw:', e);
      }
    }
  };

  // Tear down the persistent <audio> element. Called when the user is
  // released to the main app (navigate to /enneagram) and from the
  // unmount cleanup as a belt-and-suspenders against navigation paths
  // that bypass the explicit completion button.
  const cleanupIntakeAudio = () => {
    if (intakeAudioRef.current) {
      try {
        intakeAudioRef.current.pause();
      } catch {}
      try {
        intakeAudioRef.current.removeAttribute('src');
        intakeAudioRef.current.load();
      } catch {}
      intakeAudioRef.current = null;
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      console.warn('[Intake] UNMOUNT cleanup firing — Intake is being torn down');
      try {
        voice.cancel();
      } catch {}
      // inlineReleaseStream + cleanupIntakeAudio are dormant — the new
      // pipeline owns its own teardown. Day 6 removes the calls.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile: pause TTS when the screen locks / tab backgrounds.
  // AudioSessionManager also auto-suspends/resumes on visibilitychange,
  // but explicitly cancelling here drops any in-flight stream + listening
  // turn rather than leaving them paused for resume.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibilityChange = () => {
      if (document.hidden) {
        try {
          voice.cancel();
        } catch {}
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------- Render --------------
  const showSelector = inputMode === null;

  return (
    <div
      className="min-h-screen w-full ff-ui flex flex-col"
      style={{ background: PAPER, color: INK, position: 'relative' }}
    >
      <FontStyles />
      <IntakeStyles />

      {/* Fixed top stack */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          background: PAPER,
          borderBottom: `1px solid rgba(217,207,184,0.35)`,
          boxShadow: '0 6px 14px -10px rgba(27,24,20,0.18)',
        }}
      >
        <div className="px-5 pt-5 pb-2 flex items-center justify-between w-full max-w-[440px] mx-auto">
          <button
            onClick={() => {
              try {
                voice.cancel();
              } catch {}
              navigate('/guide-setup');
            }}
            className="p-2 -ml-2 rounded-full transition active:scale-95"
          >
            <ChevronLeft size={20} strokeWidth={1.5} style={{ color: INK }} />
          </button>
          <div className="flex items-center gap-2">
            <FoxMark size={18} variant="gold" />
            <span
              className="ff-display"
              style={{ color: INK, fontSize: 14, fontWeight: 500 }}
            >
              {guide?.name || 'Your guide'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {/* Mode toggle — only when a mode is selected */}
            {!showSelector && (
              <button
                onClick={inputMode === 'voice' ? switchToText : switchToVoice}
                className="p-2 rounded-full transition active:scale-95"
                aria-label={
                  inputMode === 'voice'
                    ? 'Switch to text mode'
                    : 'Switch to voice mode'
                }
                title={
                  inputMode === 'voice'
                    ? 'Switch to text mode'
                    : 'Switch to voice mode'
                }
              >
                {inputMode === 'voice' ? (
                  <Keyboard
                    size={18}
                    strokeWidth={1.6}
                    style={{ color: ACCENT }}
                  />
                ) : (
                  <Mic
                    size={18}
                    strokeWidth={1.6}
                    style={{ color: ACCENT }}
                  />
                )}
              </button>
            )}
            <button
              onClick={toggleVoice}
              aria-label={
                voiceEnabled ? 'Mute guide voice' : 'Unmute guide voice'
              }
              className="p-2 rounded-full transition active:scale-95"
              title={voiceEnabled ? 'Voice on' : 'Voice off'}
            >
              {voiceEnabled ? (
                <Volume2
                  size={18}
                  strokeWidth={1.6}
                  style={{ color: ACCENT }}
                />
              ) : (
                <VolumeX
                  size={18}
                  strokeWidth={1.6}
                  style={{ color: INK_SOFT }}
                />
              )}
            </button>
            <button className="p-2 -mr-2 rounded-full transition active:scale-95">
              <MoreHorizontal
                size={20}
                strokeWidth={1.5}
                style={{ color: INK }}
              />
            </button>
          </div>
        </div>

        <div className="px-7 pb-2 flex items-center justify-center gap-1.5 w-full max-w-[440px] mx-auto">
          <Lock size={11} strokeWidth={1.75} style={{ color: ACCENT }} />
          <span
            className="ff-mono"
            style={{
              color: GOLD_DEEP,
              fontSize: 9.5,
              letterSpacing: '0.22em',
            }}
          >
            PRIVATE & SECURE — YOUR STORY GOES NOWHERE
          </span>
        </div>

        <ProgressBar pct={progress} />
      </div>

      {/* Body */}
      {showSelector ? (
        <ModeSelector
          guideName={guide?.name}
          onSelect={handleModeSelect}
          onSelectVoice={requestMicPermissionAndEnterVoiceMode}
          micPermissionDenied={micPermissionDenied}
        />
      ) : inputMode === 'voice' && !isPreparing ? (
        // INLINE voice mode — every press handler is a native addEventListener
        // on inlineButtonRef, exactly like /voice-test (which works on iOS).
        // No HoldToSpeak component, no React synthetic events on the button.
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#F2EBDC',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 50,
          }}
        >
          {/* Progress bar — top of the column */}
          <div
            style={{
              flexShrink: 0,
              padding: '16px 20px 12px',
              background: '#F2EBDC',
              borderBottom: '1px solid rgba(166,124,58,0.20)',
            }}
          >
            <div
              style={{
                height: 4,
                background: 'rgba(166,124,58,0.15)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: '#A67C3A',
                  borderRadius: 2,
                  transition: 'width 0.7s ease',
                }}
              />
            </div>
            <p
              style={{
                fontSize: 11,
                color: '#A67C3A',
                margin: '4px 0 0',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              {progress < 30
                ? 'Getting to know you…'
                : progress < 60
                ? 'Going deeper…'
                : progress < 86
                ? 'Almost there…'
                : 'Building your study…'}
            </p>
          </div>

          {/*
            Scrollable content area. Centers its children when the
            content fits, and scrolls when it doesn't. Bottom padding
            clears the pinned Exit bar so nothing important sits hidden
            behind it (button + iOS safe-area inset).
          */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 16px calc(96px + env(safe-area-inset-bottom))',
            }}
          >
          {/* Reactive guide orb — pulses to the guide's voice */}
          <ReactiveFoxMark
            amplitude={amplitude}
            isActive={isGuideSpeaking}
            size={isGuideSpeaking ? 140 : 100}
            style={{ marginBottom: 24 }}
          />

          {/* Guide / status line */}
          <p
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 18,
              color: '#1B1814',
              marginBottom: 24,
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '0 24px',
            }}
          >
            {isGuideSpeaking
              ? `${guide?.name || 'Your guide'} is speaking…`
              : isTyping
              ? `${guide?.name || 'Your guide'} is thinking…`
              : awaitingMicGrant
              ? 'Tap Allow to continue…'
              : voice.state === 'listening'
              ? 'Listening… tap to send'
              : voice.state === 'transcribing'
              ? 'Transcribing…'
              : 'Tap to speak'}
          </p>

          {/* Retry button — surfaces when the guide hasn't replied in 30s. */}
          {showRetry && lastUserMessage && (
            <button
              type="button"
              onClick={() => {
                setShowRetry(false);
                handleSend(lastUserMessage);
              }}
              style={{
                background: 'none',
                border: '1px solid rgba(166,124,58,0.3)',
                borderRadius: 20,
                padding: '8px 16px',
                fontSize: 12,
                color: '#A67C3A',
                fontFamily: 'Arial, sans-serif',
                cursor: 'pointer',
                margin: '8px auto',
                display: 'block',
              }}
            >
              Guide did not respond — tap to retry
            </button>
          )}

          {/* The hold button — only when guide is idle and we're not in confirm */}
          {!isGuideSpeaking &&
            !isTyping &&
            !awaitingMicGrant &&
            !inlineShowConfirm && (
              <button
                ref={inlineButtonCallbackRef}
                type="button"
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  background:
                    voice.state === 'listening' ? '#8B6914' : '#F2EBDC',
                  border: '3px solid #A67C3A',
                  color:
                    voice.state === 'listening' ? '#F2EBDC' : '#A67C3A',
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: 'Arial, sans-serif',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  outline: 'none',
                  WebkitAppearance: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: 0,
                  position: 'relative',
                  zIndex: 10,
                  boxShadow:
                    voice.state === 'listening'
                      ? '0 0 0 12px rgba(139,105,20,0.25), 0 0 0 24px rgba(139,105,20,0.10)'
                      : 'none',
                  transition:
                    'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                }}
              >
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ pointerEvents: 'none' }}
                >
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
                <span style={{ pointerEvents: 'none' }}>
                  {voice.state === 'listening' ? 'TAP TO STOP' : 'TAP'}
                </span>
              </button>
            )}

          {/* Typing indicator while API is responding */}
          {isTyping && (
            <div style={{ display: 'flex', gap: 6 }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#A67C3A',
                    animation: `typing-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          )}

          {/* I heard … confirmation card. Bound to voice.currentTranscript;
              inlineShowConfirm is flipped true by the hook's onTurnComplete
              callback and reset on Send / Try again. */}
          {inlineShowConfirm && voice.currentTranscript && (
            <div
              style={{
                maxWidth: 300,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '0 20px',
              }}
            >
              <div
                style={{
                  background: '#F2EBDC',
                  border: '1px solid rgba(166,124,58,0.30)',
                  borderRadius: 12,
                  padding: '12px 16px',
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color: '#A67C3A',
                    fontFamily: 'monospace',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    margin: '0 0 6px',
                  }}
                >
                  I heard
                </p>
                <p
                  style={{
                    fontSize: 15,
                    color: '#1B1814',
                    margin: 0,
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    lineHeight: 1.5,
                  }}
                >
                  "{voice.currentTranscript}"
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    // Dismiss the card. The next listen → transcribe
                    // cycle will overwrite voice.currentTranscript on
                    // its own, so no explicit clear is needed.
                    setInlineShowConfirm(false);
                  }}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 10,
                    background: 'transparent',
                    border: '1.5px solid #A67C3A',
                    color: '#A67C3A',
                    fontSize: 14,
                    cursor: 'pointer',
                    fontWeight: 600,
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                  }}
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const t = voice.currentTranscript;
                    console.log(
                      '[Inline] Send button tapped, transcript:',
                      t?.substring(0, 50)
                    );
                    setInlineShowConfirm(false);
                    if (sendUserResponseRef.current && t) {
                      sendUserResponseRef.current(t);
                    }
                  }}
                  style={{
                    flex: 2,
                    padding: 12,
                    borderRadius: 10,
                    background: '#1B1814',
                    border: 'none',
                    color: '#F2EBDC',
                    fontSize: 14,
                    cursor: 'pointer',
                    fontWeight: 600,
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: 16,
                padding: '10px 14px',
                background: 'rgba(91,42,46,0.08)',
                border: '1px solid rgba(91,42,46,0.25)',
                color: '#5B2A2E',
                borderRadius: 10,
                fontSize: 12,
                maxWidth: 320,
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}
          </div>

          {/*
            Exit bar — fixed to the viewport bottom with a solid
            background so any content scrolling behind it stays
            hidden. Safe-area inset on the bottom keeps the button
            above the iOS home indicator.
          */}
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding:
                '16px 24px calc(16px + env(safe-area-inset-bottom))',
              background: '#F2EBDC',
              borderTop: '1px solid rgba(166,124,58,0.20)',
              boxShadow: '0 -6px 14px -10px rgba(27,24,20,0.18)',
              zIndex: 60,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={switchToText}
              style={{
                background: 'none',
                border: '1px solid #A67C3A',
                color: '#A67C3A',
                padding: '10px 24px',
                borderRadius: 20,
                fontSize: 14,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
              }}
            >
              Exit voice mode
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className="flex-1 overflow-y-auto scrollbar-hide w-full max-w-[440px] mx-auto px-5"
            style={{
              paddingTop: HEADER_STACK_HEIGHT + 12,
              // Enough headroom that the last guide message clears the
              // input bar (and the retry button when it's surfaced).
              paddingBottom: 140,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {intakeMessages.map((m, i) => {
              const isLatestGuide =
                m.role === 'assistant' &&
                i ===
                  intakeMessages.length -
                    1 -
                    [...intakeMessages]
                      .reverse()
                      .findIndex((x) => x.role === 'assistant');
              return m.role === 'assistant' ? (
                <Fragment key={i}>
                  <GuideMessage
                    content={m.content}
                    guideName={guide?.name}
                    isLatest={isLatestGuide}
                    isSpeaking={isSpeaking}
                    isLoading={isVoiceLoading}
                  />
                  {detectCrisis(m.content) && <CrisisResourceCard />}
                </Fragment>
              ) : (
                <UserMessage key={i} content={m.content} />
              );
            })}
            {isTyping && <TypingIndicator guideName={guide?.name} />}

            {error && (
              <div
                className="mt-4 p-3 rounded-xl ff-ui text-[12px] leading-[1.5] text-center"
                style={{
                  background: 'rgba(91,42,46,0.08)',
                  border: `1px solid rgba(91,42,46,0.25)`,
                  color: '#5B2A2E',
                }}
              >
                {error}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {!showContinueButton && (
            <div
              className="w-full max-w-[440px] mx-auto"
              style={{ flexShrink: 0 }}
            >
              {showRetry && lastUserMessage && (
                <button
                  type="button"
                  onClick={() => {
                    setShowRetry(false);
                    handleSend(lastUserMessage);
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid rgba(166,124,58,0.3)',
                    borderRadius: 20,
                    padding: '8px 16px',
                    fontSize: 12,
                    color: '#A67C3A',
                    fontFamily: 'Arial, sans-serif',
                    cursor: 'pointer',
                    margin: '8px auto',
                    display: 'block',
                  }}
                >
                  Guide did not respond — tap to retry
                </button>
              )}
              <InputBar
                onSend={handleSend}
                disabled={isTyping || isPreparing}
              />
              <VoiceStatusPill guideGender={guide?.gender} />
            </div>
          )}
        </>
      )}

      {/* Old completion-flow render blocks (Continue button bar +
          completion overlay with transition + disclaimer cards) were
          removed when the isComplete branch in handleSend gained
          auto-speak + direct /enneagram nav. The CompletionMessage
          component, COMPLETION_TRANSITION_TEXT / COMPLETION_DISCLAIMER_TEXT
          constants, runCompletionSequence, handleContinueTap, and the
          showContinueButton / showCompletionScreen / completionStep
          useStates stay declared (dormant) — Day 7 deletes the lot. */}

      {isPreparing && (
        <PreparingScreen
          guideName={guide?.name}
          errorMessage={preparingErrorMessage}
        />
      )}
    </div>
  );
}
